import * as React from 'react';
import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import StatCard from './StatCard';
import { getAllSessions } from '../api/sessions';
import dayjs from 'dayjs';
import PageViewsBarChart from './PageViewsBarChart';
import ChartUserByCountry from './ChartUserByCountry';
import Stack from '@mui/material/Stack';
import ChartTipsByBarber from './ChartTipsByBarber';

export default function MainGrid({ selectedDate }) {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [alertState, setAlertState] = useState(null);

  const currentWeekEnd = new Date(selectedDate);
  const currentWeekStart = new Date(currentWeekEnd);
  currentWeekStart.setDate(currentWeekEnd.getDate() - 6); // Last 7 days
  const previousWeekEnd = new Date(currentWeekStart);
  previousWeekEnd.setDate(currentWeekStart.getDate() - 1); // End of previous week
  const previousWeekStart = new Date(previousWeekEnd);
  previousWeekStart.setDate(previousWeekEnd.getDate() - 6); // Start of previous week

  const startDate = dayjs(currentWeekStart).format('YYYY-MM-DD');
  const duration = 7;

  // Data Fetching
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const data = await getAllSessions();
        console.log('Fetched report data:', data);

        if (data?.length > 0) {
          setReportData(data);
        } else {
          setAlertState({ message: 'No sessions found.', severity: 'info' });
          setTimeout(() => setAlertState(null), 5000);
        }
      } catch (error) {
        console.error('Error fetching report data:', error.message);
        setAlertState({ message: 'Failed to load report.', severity: 'error' });
        setTimeout(() => setAlertState(null), 5000);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, []);

  // Calculations
  const calculatePeriodStats = (start, end) => {
    const stats = {
      totalIncome: 0,
      totalTips: 0,
      count: 0,
    };

    reportData.forEach((entry) => {
      const entryDate = new Date(entry.date);
      if (entryDate >= start && entryDate <= end) {
        const price = entry.total_price || 0;
        const tips = entry.tip || 0;
        const extra = entry.extra_money || 0;
        stats.totalIncome += price + tips + extra;
        stats.totalTips += tips;
        stats.count += 1;
      }
    });

    return stats;
  };

  const currentWeekStats = calculatePeriodStats(currentWeekStart, currentWeekEnd);
  const previousWeekStats = calculatePeriodStats(previousWeekStart, previousWeekEnd);

  const determineTrend = (current, previous) => {
    if (previous === 0 && current > 0) return 'up';
    if (current === 0 && previous === 0) return 'neutral';
    if (previous === 0) return 'down';
    
    const change = ((current - previous) / previous) * 100;
    if (change > 5) return 'up';
    if (change < -5) return 'down';
    return 'neutral';
  };

  const calculateDailyAverage = (selectedDate) => {
    const endDate = new Date(selectedDate);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);

    const dailyStats = {};
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      dailyStats[dateStr] = {
        totalIncome: 0,
        totalPrice: 0,
        totalTips: 0,
        totalExtra: 0,
        count: 0,
        average: 0,
      };
    }

    reportData.forEach((entry) => {
      const entryDate = new Date(entry.date);
      const dateStr = entryDate.toISOString().split('T')[0];
      if (entryDate >= startDate && entryDate <= endDate && dailyStats[dateStr]) {
        const price = entry.total_price || 0;
        const tips = entry.tip || 0;
        const extra = entry.extra_money || 0;
        dailyStats[dateStr].totalPrice += price;
        dailyStats[dateStr].totalTips += tips;
        dailyStats[dateStr].totalExtra += extra;
        dailyStats[dateStr].totalIncome += price + tips + extra;
        dailyStats[dateStr].count += 1;
      }
    });

    Object.keys(dailyStats).forEach((date) => {
      const stats = dailyStats[date];
      stats.average = stats.count > 0 ? stats.totalIncome / stats.count : 0;
    });

    return Object.entries(dailyStats)
      .map(([date, stats]) => ({
        date,
        totalIncome: Number(stats.totalIncome.toFixed(2)),
        totalPrice: Number(stats.totalPrice.toFixed(2)),
        totalTips: Number(stats.totalTips.toFixed(2)),
        totalExtra: Number(stats.totalExtra.toFixed(2)),
        count: stats.count,
        average: Number(stats.average.toFixed(2)),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const calculateDailyTips = (selectedDate) => {
    const endDate = new Date(selectedDate);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);

    const dailyTips = {};
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      dailyTips[dateStr] = {
        totalTips: 0,
        count: 0,
        average: 0,
      };
    }

    reportData.forEach((entry) => {
      const entryDate = new Date(entry.date);
      const dateStr = entryDate.toISOString().split('T')[0];
      if (entryDate >= startDate && entryDate <= endDate && dailyTips[dateStr]) {
        const tips = entry.tip || 0;
        dailyTips[dateStr].totalTips += tips;
        dailyTips[dateStr].count += 1;
      }
    });

    Object.keys(dailyTips).forEach((date) => {
      const stats = dailyTips[date];
      stats.average = stats.count > 0 ? stats.totalTips / stats.count : 0;
    });

    return Object.entries(dailyTips)
      .map(([date, stats]) => ({
        date,
        totalTips: Number(stats.totalTips.toFixed(2)),
        count: stats.count,
        average: Number(stats.average.toFixed(2)),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Data for StatCards
  const dailyAverages = calculateDailyAverage(selectedDate);
  const dailyTips = calculateDailyTips(selectedDate);

  const incomeStat = {
    title: 'Total Income',
    value: `€${currentWeekStats.totalIncome.toFixed(2)}`,
    interval: `Compared to Last Week - ${dayjs(selectedDate).subtract(14, 'day').format('MMM D')} to ${dayjs(selectedDate).subtract(7, 'day').format('MMM D')}`,
    trend: determineTrend(currentWeekStats.totalIncome, previousWeekStats.totalIncome),
    difference: `€${currentWeekStats.totalIncome - previousWeekStats.totalIncome.toFixed(2)}`,
    data: dailyAverages.map((item) => item.average),
    startDate,
    duration,
  };

  const tipsStat = {
    title: 'Total Tips',
    value: `€${currentWeekStats.totalTips.toFixed(2)}`,
    interval: `Compared to Last Week - ${dayjs(selectedDate).subtract(14, 'day').format('MMM D')} to ${dayjs(selectedDate).subtract(7, 'day').format('MMM D')}`,
    trend: determineTrend(currentWeekStats.totalTips, previousWeekStats.totalTips),
    data: dailyTips.map((item) => item.average),
    difference: `€${currentWeekStats.totalTips - previousWeekStats.totalTips.toFixed(2)}`,
    startDate,
    duration,
  };

  // Render
  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview
      </Typography>

      <Stack container spacing={2} sx={{ mb: 2 }} direction='row'>
        <Stack sx={{ flexGrow: 1 }} spacing={2}>
          <StatCard {...incomeStat} />
          <StatCard {...tipsStat} />
        </Stack>
        <ChartUserByCountry selectedDate={selectedDate} />
      </Stack>

      <Typography component="h2" variant="h6" sx={{ mb: 3, fontWeight: 500, mt:4 }}>
        Rico' statistics
      </Typography>

      <Stack container spacing={2} sx={{ mb: 2 }} direction='row'>
          <PageViewsBarChart selectedDate={selectedDate} barberName={'Rico'}/>
          <ChartTipsByBarber selectedDate={selectedDate} barberName={'Rico'} />
      </Stack>

      <Typography component="h2" variant="h6" sx={{ mb: 3, fontWeight: 500, mt:4 }}>
        Resan's statistics
      </Typography>
      <Stack container spacing={2} sx={{ mb: 2 }} direction='row'>
          <PageViewsBarChart selectedDate={selectedDate} barberName={'Resan'}/>
          <ChartTipsByBarber selectedDate={selectedDate} barberName={'Resan'} />
      </Stack>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}